import React, { type ReactElement, useCallback, useRef } from 'react';

import { cx } from 'cva';
import { Header } from 'react-aria-components/Header';
import type {
  MenuProps as RACMenuProps,
  Selection,
} from 'react-aria-components/Menu';
import {
  Menu as RACMenu,
  SubmenuTrigger as RACSubmenuTrigger,
} from 'react-aria-components/Menu';

import { useSelectedKeys } from '../../hooks/use_selected_keys';
import {
  type MultipleSelectionProps,
  type SingleSelectionProps,
  toItemKeys,
  toSelectedKeys,
} from '../../utils/selection_change';
import { Popover, type PopoverProps } from '../popover/popover';
import { Separator } from '../separator/separator';
import { MenuItem, type MenuItemProps } from './menu_item';

import styles from './menu.module.css';

export type MenuItemWithId<ItemKey extends string = string> = Omit<
  MenuItemProps,
  'id' | 'submenuChildren'
> & {
  id: ItemKey;
  submenuChildren?: MenuItemWithId<ItemKey>[];
};

export type MenuSelectionProps<ItemKey extends string> =
  | ({ selectionMode: 'single' } & SingleSelectionProps<ItemKey>)
  | ({ selectionMode?: 'multiple' | 'none' } & MultipleSelectionProps<ItemKey>);

export type MenuOptionsProps<ItemKey extends string = string> = Omit<
  RACMenuProps<MenuItemProps>,
  | 'items'
  | 'selectionMode'
  | 'selectedKeys'
  | 'defaultSelectedKeys'
  | 'disallowEmptySelection'
  | 'onSelectionChange'
> & {
  className?: string;
  /**
   * Optional header title to display at the top of the context menu.
   */
  headerTitle?: string;
  /**
   * Optional header element, included in the top right of the context menu.
   * Mostly used for a Button element.
   */
  headerActionElement?: ReactElement;

  /**
   * The items to display in the context menu.
   */
  items: MenuItemWithId<ItemKey>[];

  /**
   * Callback for when the context menu is opened or closed.
   */
  onOpenChange?: (value: boolean) => void;
  /**
   * isOpen state value
   */
  isOpen?: boolean;
  /**
   * Optional RAC popover props to pass to the underlying popover component.
   */
  popoverProps?: PopoverProps;
  /**
   * When true, the menu is capped to a max width and item labels wrap onto
   * multiple lines instead of forcing the menu to grow horizontally.
   */
  wrapItemLabels?: boolean;
};

export type MenuProps<ItemKey extends string = string> =
  MenuOptionsProps<ItemKey> & MenuSelectionProps<ItemKey>;

export function Menu<ItemKey extends string = string>(
  props: MenuProps<ItemKey>,
) {
  const {
    className,
    headerTitle,
    headerActionElement,
    items,
    popoverProps,
    selection,
    defaultSelection,
    wrapItemLabels,
    ...menuProps
  } = props;
  const selectedKeys = useSelectedKeys(selection);
  const handleSelectionChange = (newSelection: Selection) => {
    const keys = toItemKeys<ItemKey>(
      newSelection === 'all' ? items.map((item) => item.id) : newSelection,
    );
    const [key] = keys;
    if (props.selectionMode !== 'single') {
      props.onSelectionChange?.(keys);
    } else if (props.disallowEmptySelection) {
      if (key !== undefined) {
        props.onSelectionChange?.(key);
      }
    } else {
      props.onSelectionChange?.(key ?? null);
    }
  };
  const popoverContentClassName = cx(styles.popover_content, {
    [styles.popover_content_wrapping]: wrapItemLabels,
  });
  // When the menu is dismissed by pressing outside, react-aria's focus scope
  // briefly pulls focus back into the menu, which makes it highlight its
  // first item right before closing. While a pointer is held down outside,
  // stop focus events from reaching react-aria to avoid that flash. A
  // callback ref is used because the popover content mounts only when the
  // menu opens.
  const removeListenersRef = useRef<(() => void) | null>(null);
  const contentRef = useCallback((contentElement: HTMLDivElement | null) => {
    removeListenersRef.current?.();
    removeListenersRef.current = null;
    if (!contentElement) {
      return;
    }
    let isPointerDownOutside = false;
    const onDocumentPointerDown = (event: PointerEvent) => {
      // The target can be a non-Element node (e.g. Text), so containment is
      // checked on the node itself. Submenus render in their own portals
      // outside this popover, so a press inside any menu counts as inside.
      const targetNode = event.target instanceof Node ? event.target : null;
      const targetElement =
        targetNode instanceof Element ? targetNode : targetNode?.parentElement;
      isPointerDownOutside =
        targetNode !== null &&
        !contentElement.contains(targetNode) &&
        (targetElement?.closest('[role="menu"]') ?? null) === null;
    };
    const resetPointerDownOutside = () => {
      isPointerDownOutside = false;
    };
    const onFocusIn = (event: FocusEvent) => {
      if (isPointerDownOutside) {
        event.stopPropagation();
      }
    };
    document.addEventListener('pointerdown', onDocumentPointerDown, true);
    document.addEventListener('pointerup', resetPointerDownOutside, true);
    document.addEventListener('pointercancel', resetPointerDownOutside, true);
    contentElement.addEventListener('focusin', onFocusIn, true);
    removeListenersRef.current = () => {
      document.removeEventListener('pointerdown', onDocumentPointerDown, true);
      document.removeEventListener('pointerup', resetPointerDownOutside, true);
      document.removeEventListener(
        'pointercancel',
        resetPointerDownOutside,
        true,
      );
      contentElement.removeEventListener('focusin', onFocusIn, true);
    };
  }, []);

  return (
    <Popover className={className} {...popoverProps}>
      <div className={popoverContentClassName} ref={contentRef}>
        {headerTitle || headerActionElement ? (
          <>
            <div className={styles.header}>
              <Header className={styles.header_title}>{headerTitle}</Header>
              {headerActionElement ? (
                <Header className={styles.header_action}>
                  {headerActionElement}
                </Header>
              ) : null}
            </div>
            <Separator variant="sub" />
          </>
        ) : null}
        <RACMenu
          selectedKeys={selectedKeys}
          defaultSelectedKeys={toSelectedKeys(defaultSelection)}
          className={popoverContentClassName}
          items={items}
          {...menuProps}
          onSelectionChange={handleSelectionChange}
        >
          {function renderSubmenu(menuItem: MenuItemProps) {
            if (menuItem.isSeparator && menuItem.headerTitle) {
              return (
                <React.Fragment key={menuItem.id}>
                  <div className={styles.header_title}>
                    <Header className={styles.header_title}>
                      {menuItem.headerTitle}
                    </Header>
                  </div>
                  <Separator variant="sub" key={menuItem.id} />
                </React.Fragment>
              );
            }
            if (menuItem.isSeparator && !menuItem.headerTitle) {
              return <Separator variant="sub" key={menuItem.id} />;
            }

            const { id, ...menuItemProps } = menuItem;

            if (menuItem.submenuChildren) {
              return (
                <RACSubmenuTrigger>
                  <MenuItem key={id} {...menuItemProps} />
                  <Popover className={className}>
                    <RACMenu
                      selectedKeys={selectedKeys}
                      defaultSelectedKeys={toSelectedKeys(defaultSelection)}
                      items={menuItem.submenuChildren}
                      className={popoverContentClassName}
                      {...menuProps}
                      onSelectionChange={handleSelectionChange}
                    >
                      {(submenuItem: MenuItemProps) =>
                        renderSubmenu(submenuItem)
                      }
                    </RACMenu>
                  </Popover>
                </RACSubmenuTrigger>
              );
            }

            return <MenuItem key={id} {...menuItemProps} />;
          }}
        </RACMenu>
      </div>
    </Popover>
  );
}
